import {
  DEFAULT_TIMEZONE,
  getCurrentTimezone,
  type AppTimezone,
} from "@/i18n/config";

interface CivilDateTime {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
}

function zonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
  return { year: read("year"), month: read("month"), day: read("day"), hour: read("hour"), minute: read("minute"), second: read("second") };
}

export function parseCivilDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new RangeError(`Invalid civil date: ${value}`);
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

export function addCivilDays(value: string, days: number) {
  const civil = parseCivilDate(value);
  return new Date(Date.UTC(civil.year, civil.month - 1, civil.day + days)).toISOString().slice(0, 10);
}

export function civilDateTimeToUtc(
  value: CivilDateTime,
  timeZone: AppTimezone = getCurrentTimezone(),
): Date {
  const target = Date.UTC(value.year, value.month - 1, value.day, value.hour ?? 0, value.minute ?? 0, value.second ?? 0);
  let candidate = target;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = zonedParts(new Date(candidate), timeZone);
    const represented = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    const adjustment = target - represented;
    candidate += adjustment;
    if (adjustment === 0) break;
  }
  const result = new Date(candidate);
  const actual = zonedParts(result, timeZone);
  const expected = { ...value, hour: value.hour ?? 0, minute: value.minute ?? 0, second: value.second ?? 0 };
  if (Object.keys(expected).some((key) => actual[key as keyof typeof actual] !== expected[key as keyof typeof expected])) {
    throw new RangeError("The civil time does not exist in the clinic timezone");
  }
  return result;
}

export function civilInputToUtc(date: string, time = "00:00", timeZone: AppTimezone = getCurrentTimezone()) {
  const civil = parseCivilDate(date);
  const [hour, minute, second = 0] = time.split(":").map(Number);
  return civilDateTimeToUtc({ ...civil, hour, minute, second }, timeZone);
}

export function toHalfOpenClinicPeriod(
  startDate: string,
  inclusiveEndDate: string,
  timeZone: AppTimezone = getCurrentTimezone(),
) {
  return {
    start: civilInputToUtc(startDate, "00:00", timeZone).toISOString(),
    end: civilInputToUtc(addCivilDays(inclusiveEndDate, 1), "00:00", timeZone).toISOString(),
  };
}

export function resolveClinicTimezone(value?: string | null): AppTimezone {
  return value && ["America/Sao_Paulo", "America/New_York", "Europe/Lisbon", "Europe/Madrid", "Europe/Rome"].includes(value)
    ? value as AppTimezone
    : DEFAULT_TIMEZONE;
}
